import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add any auth tokens here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// API Key endpoints
export const apiKeyService = {
  // Generate new API key
  generateApiKey: (data) => api.post('/keys/generate', data),
  
  // Get all API keys
  getApiKeys: (userId) => api.get('/keys', { params: { userId } }),
  
  // Get specific API key
  getApiKey: (keyId) => api.get(`/keys/${keyId}`),
  
  // Validate API key
  validateApiKey: (keyValue) => api.post('/keys/validate', { keyValue }),
  
  // Deactivate API key
  deactivateApiKey: (keyId) => api.patch(`/keys/${keyId}/deactivate`),
  
  // Reactivate API key
  reactivateApiKey: (keyId) => api.patch(`/keys/${keyId}/reactivate`),
  
  // Delete API key
  deleteApiKey: (keyId) => api.delete(`/keys/${keyId}`),
  
  // Update API key
  updateApiKey: (keyId, data) => api.patch(`/keys/${keyId}`, data),

  // Aliases used by ApiKeys.js component
  getAll: () => api.get("/keys"),
  generate: (data) => api.post("/keys/generate", data),
  delete: (keyId) => api.delete(`/keys/${keyId}`),
  extendLimit: (keyId, newLimit) => api.patch(`/keys/${keyId}/extend-limit`, { newLimit }),
};

// Usage endpoints
export const usageService = {
  // Get usage statistics for a specific API key
  getUsageStats: (keyId, startDate, endDate) => 
    api.get(`/usage/stats/${keyId}`, { params: { startDate, endDate } }),
  
  // Get usage logs for a specific API key
  getUsageLogs: (keyId, limit, offset) => 
    api.get(`/usage/logs/${keyId}`, { params: { limit, offset } }),
  
  // Get overall usage statistics
  getUsageOverview: (startDate, endDate, cacheBuster) => 
    api.get('/usage/overview', { params: { startDate, endDate, _t: cacheBuster } }),
  
  // Get daily usage trends
  getUsageTrends: (days) => api.get('/usage/trends', { params: { days } }),
  
  // Get model-specific usage
  getModelUsage: (startDate, endDate) => 
    api.get('/usage/models', { params: { startDate, endDate } }),
};

// Analytics endpoints
export const analyticsService = {
  // Get cost analysis
  getCostAnalysis: (startDate, endDate, groupBy) => 
    api.get('/analytics/costs', { params: { startDate, endDate, groupBy } }),
  
  // Get performance metrics
  getPerformanceMetrics: (startDate, endDate) => 
    api.get('/analytics/performance', { params: { startDate, endDate } }),
  
  // Get usage patterns
  getUsagePatterns: (startDate, endDate) => 
    api.get('/analytics/patterns', { params: { startDate, endDate } }),
  
  // Get forecasting data
  getForecastData: (days) => api.get('/analytics/forecast', { params: { days } }),
  
  // Get real-time metrics
  getRealTimeMetrics: () => api.get('/analytics/realtime'),
};

// Health check
export const healthService = {
  checkHealth: () => api.get('/health'),
};

// Settings
export const settingsService = {
  updateSettings: (data) => api.post('/settings', data),
};

// Authentication
export const authService = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { currentPassword, newPassword }),
};

// Users
export const userService = {
  getUsers: (params) => api.get('/users', { params }),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (data) => api.post('/users', data),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  deleteUser: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { newPassword }),
};

// Activity
export const activityService = {
  getRecentActivity: (limit = 10) => api.get('/activity/recent', { params: { limit } }),
  getActivityByType: (type, limit = 20) => api.get(`/activity/by-type/${type}`, { params: { limit } }),
};

// Usage History
export const usageHistoryService = {
  getUsageHistory: (params = {}) => api.get('/usage-history', { params }),
  getUsageHistorySummary: (params = {}) => api.get('/usage-history/summary', { params }),
  getTopKeysByUsage: (params = {}) => api.get('/usage-history/top-keys', { params }),
  getTopModelsByUsage: (params = {}) => api.get('/usage-history/top-models', { params }),
  getKeyNameSuggestions: (q) => api.get('/usage-history/key-names', { params: { q } }),
};


// Request Logs
export const logsService = {
  getLogs: (params = {}) => api.get('/logs', { params }),
};
export default api;

// Model catalog
export const modelService = {
  getModelCatalog: () => api.get("/models/catalog"),
};

